import { createSlice, isPending, isRejected } from '@reduxjs/toolkit';
import { RootState } from '../..';
import {
  getSubmissionsGroupedByStudents,
  getSubmissionsByCourse,
  getSubmissionsByStudent,
} from './api';
import { ISubmission } from '../../SubmissionSlice';

interface ITopicForCourseSubmission {
  topicName: string;
  submissions: ISubmission[];
  averageScore: number;
}

export interface ICourseSubmissionsData {
  groupedSubmissions: ITopicForCourseSubmission[];
  totalAverageScore: number;
  totalAverageTime: number;
  studentsCount?: number;
}

export interface ITopicSubmission {
  topicName: string;
  submissions: ISubmission[];
  averageScore: number;
  averageTime: number;
  topicId?: string;
}

export interface IStudentSubmissionsData {
  groupedSubmissions: ITopicSubmission[];
  totalAverageScore: number;
  totalAverageTime: number;
}

export interface IStudentPerformanceData {
  _id: string;
  firstname: string;
  lastname: string;
  avatar?: string;
  topics: ITopicSubmission[];
  averageScore: number;
  averageTime: number;
  lastSubmissionAt: string;
}

export interface ISubmissionsState {
  status: 'idle' | 'loading' | 'rejected';
  courseSubmissionsData: ICourseSubmissionsData | undefined;
  studentSubmissionsData: IStudentSubmissionsData | undefined;
  groupedStudentsSubmissions: IStudentPerformanceData[] | undefined;
}

const initialState: ISubmissionsState = {
  status: 'idle',
  courseSubmissionsData: undefined,
  studentSubmissionsData: undefined,
  groupedStudentsSubmissions: undefined,
};

export const SubmissionsSlice = createSlice({
  name: 'submissionsV2',
  initialState,
  reducers: {
    resetCourseSubmissionsData: (state) => {
      state.courseSubmissionsData = undefined;
    },
    resetStudentSubmissionsData: (state) => {
      state.studentSubmissionsData = undefined;
    },
    resetGroupedStudentsSubmissions: (state) => {
      state.groupedStudentsSubmissions = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET SUBMISSIONS BY COURSE
      .addCase(getSubmissionsByCourse.fulfilled, (state, action) => {
        state.status = 'idle';
        state.courseSubmissionsData = action.payload;
      })
      // GET SUBMISSIONS BY STUDENT
      .addCase(getSubmissionsByStudent.fulfilled, (state, action) => {
        state.status = 'idle';
        state.studentSubmissionsData = action.payload;
      })
      // GET SUBMISSIONS GROUPED BY STUDENTS
      .addCase(getSubmissionsGroupedByStudents.fulfilled, (state, action) => {
        state.status = 'idle';
        state.groupedStudentsSubmissions = action.payload;
      });

    const commonActions = [
      getSubmissionsByCourse,
      getSubmissionsByStudent,
      getSubmissionsGroupedByStudents,
    ] as const;

    builder
      // MATCHER
      .addMatcher(isPending(...commonActions), (state) => {
        state.status = 'loading';
      })
      .addMatcher(isRejected(...commonActions), (state) => {
        state.status = 'rejected';
      });
  },
});

export const getSubmissionsV2State = (
  state: RootState
): RootState['submissionsV2'] => state.submissionsV2;

export const {
  resetCourseSubmissionsData,
  resetStudentSubmissionsData,
  resetGroupedStudentsSubmissions,
} = SubmissionsSlice.actions;

export * from './api';
export default SubmissionsSlice.reducer;
